/**
 * Triangle Checkers AI using Minimax with Alpha-Beta Pruning
 * Optimized for Chinese Checkers style gameplay
 */

const DIFFICULTY_SETTINGS = {
  easy: { depth: 2, randomness: 0.4 },
  medium: { depth: 3, randomness: 0.2 },
  hard: { depth: 4, randomness: 0.1 },
  expert: { depth: 5, randomness: 0 }
};

// Evaluation weights
const GOAL_ZONE_BONUS = 100;
const PROGRESS_BONUS = 5;
const CENTER_CONTROL = 2;
const MOBILITY_BONUS = 1;
const JUMP_CHAIN_BONUS = 3;

function evaluateBoard(game, aiPlayer) {
  let score = 0;
  const opponent = aiPlayer === 1 ? 2 : 1;
  
  // Target zones
  const aiGoalZone = aiPlayer === 1 ? 'point3' : 'point0';
  const oppGoalZone = opponent === 1 ? 'point3' : 'point0';
  
  // AI starting zone (where opponent needs to go)
  const aiStartZone = aiPlayer === 1 ? 'point0' : 'point3';
  
  for (const [key, piece] of game.pieces) {
    const cell = game.cells.get(key);
    if (!cell) continue;
    
    const isAI = piece.player === aiPlayer;
    const multiplier = isAI ? 1 : -1;
    
    // Base score for having a piece
    let pieceScore = 10;
    
    // Major bonus for pieces in goal zone
    if (cell.zone === (isAI ? aiGoalZone : oppGoalZone)) {
      pieceScore += GOAL_ZONE_BONUS;
    }
    
    // Progress bonus - reward moving toward goal
    // For player 1: lower r is better (moving toward point3 means higher r)
    // For player 2: higher r is better (moving toward point0 means lower r)
    if (isAI) {
      if (aiPlayer === 1) {
        // Player 1 wants to go down (increase r)
        pieceScore += cell.r * PROGRESS_BONUS;
      } else {
        // Player 2 wants to go up (decrease r)
        pieceScore += (-cell.r) * PROGRESS_BONUS;
      }
    } else {
      if (opponent === 1) {
        pieceScore += cell.r * PROGRESS_BONUS;
      } else {
        pieceScore += (-cell.r) * PROGRESS_BONUS;
      }
    }
    
    // Center control bonus (stay central for flexibility)
    const distFromCenter = Math.abs(cell.q) + Math.abs(cell.r);
    if (cell.zone === 'center') {
      pieceScore += (8 - distFromCenter) * CENTER_CONTROL;
    }
    
    score += pieceScore * multiplier;
  }
  
  // Mobility bonus - more moves available is better
  const originalPlayer = game.currentPlayer;
  
  game.currentPlayer = aiPlayer;
  const aiMoves = game.getAllValidMoves().length;
  
  game.currentPlayer = opponent;
  const oppMoves = game.getAllValidMoves().length;
  
  game.currentPlayer = originalPlayer;
  
  score += (aiMoves - oppMoves) * MOBILITY_BONUS;
  
  // Game over bonus
  if (game.gameOver) {
    if (game.winner === aiPlayer) {
      score += 10000;
    } else {
      score -= 10000;
    }
  }
  
  return score;
}

function minimax(game, depth, alpha, beta, maximizing, aiPlayer) {
  if (depth === 0 || game.gameOver) {
    return { score: evaluateBoard(game, aiPlayer), move: null };
  }
  
  const moves = game.getAllValidMoves();
  
  if (moves.length === 0) {
    return { score: evaluateBoard(game, aiPlayer), move: null };
  }
  
  // Sort moves to prioritize jumps (better alpha-beta pruning)
  moves.sort((a, b) => {
    const aJump = a.to.isJump ? 1 : 0;
    const bJump = b.to.isJump ? 1 : 0;
    return bJump - aJump;
  });
  
  let bestMove = moves[0];
  
  if (maximizing) {
    let maxScore = -Infinity;
    
    for (const move of moves) {
      const cloned = game.clone();
      cloned.makeMove(move.from.q, move.from.r, move.to.q, move.to.r);
      
      const { score } = minimax(cloned, depth - 1, alpha, beta, false, aiPlayer);
      
      // Prefer longer jump chains
      const jumpBonus = move.to.isJump && move.to.path ? move.to.path.length * JUMP_CHAIN_BONUS : 0;
      const adjustedScore = score + jumpBonus;
      
      if (adjustedScore > maxScore) {
        maxScore = adjustedScore;
        bestMove = move;
      }
      
      alpha = Math.max(alpha, adjustedScore);
      if (beta <= alpha) break;
    }
    
    return { score: maxScore, move: bestMove };
  } else {
    let minScore = Infinity;
    
    for (const move of moves) {
      const cloned = game.clone();
      cloned.makeMove(move.from.q, move.from.r, move.to.q, move.to.r);
      
      const { score } = minimax(cloned, depth - 1, alpha, beta, true, aiPlayer);
      
      if (score < minScore) {
        minScore = score;
        bestMove = move;
      }
      
      beta = Math.min(beta, score);
      if (beta <= alpha) break;
    }
    
    return { score: minScore, move: bestMove };
  }
}

export function getBestMove(game, difficulty = 'medium') {
  const settings = DIFFICULTY_SETTINGS[difficulty] || DIFFICULTY_SETTINGS.medium;
  const aiPlayer = game.currentPlayer;
  
  const moves = game.getAllValidMoves();
  if (moves.length === 0) return null;
  if (moves.length === 1) return moves[0];
  
  // Add randomness for lower difficulties
  if (Math.random() < settings.randomness) {
    // Prefer forward-moving random moves
    const forwardMoves = moves.filter(m => {
      if (aiPlayer === 1) {
        return m.to.r > m.from.r; // Moving down
      } else {
        return m.to.r < m.from.r; // Moving up
      }
    });
    
    const pool = forwardMoves.length > 0 ? forwardMoves : moves;
    return pool[Math.floor(Math.random() * pool.length)];
  }
  
  const { move } = minimax(game, settings.depth, -Infinity, Infinity, true, aiPlayer);
  return move;
}

export function executeAIMove(game, difficulty = 'medium') {
  const move = getBestMove(game, difficulty);
  if (!move) return null;
  
  const success = game.makeMove(move.from.q, move.from.r, move.to.q, move.to.r);
  
  if (success) {
    return {
      from: move.from,
      to: { q: move.to.q, r: move.to.r },
      path: move.to.path
    };
  }
  
  return null;
}

export default { getBestMove, executeAIMove };
